import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api/client.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

type TipsReport = {
  totalTips: string;
  tipCount: number;
  byEmployee: Array<{
    userId: string | null;
    name: string;
    count: number;
    total: string;
    average: string;
    salesExcludingTips: string;
    tipsAsPercentOfSales: number | null;
  }>;
  byMethod: Array<{ method: string; count: number; total: string }>;
  byDay: Array<{ date: string; count: number; total: string }>;
};

function fetchTipsReport(params: {
  period: string;
  storeId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const qs = new URLSearchParams();
  qs.set('period', params.period);
  if (params.storeId) qs.set('storeId', params.storeId);
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
  if (params.dateTo) qs.set('dateTo', params.dateTo);
  return apiGet<TipsReport>(`/api/v1/reports/tips?${qs}`).then((r) => r.data);
}

export function TipsReportPage() {
  const f = useReportFilters();
  const q = useQuery({
    queryKey: ['tips-report', f.period, f.storeId, f.dateFrom, f.dateTo],
    queryFn: () =>
      fetchTipsReport({
        period: f.period,
        storeId: f.storeId || undefined,
        dateFrom: f.dateFrom || undefined,
        dateTo: f.dateTo || undefined,
      }),
  });

  if (q.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading tips…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        Could not load tips report.
      </div>
    );
  }

  const data = q.data;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-500">Total tips</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{formatCurrency(data.totalTips)}</p>
          <p className="text-sm text-slate-500">{data.tipCount} tip payment(s)</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5 text-sm text-amber-950">
          Tips are a liability (held for staff), not revenue. They are excluded from P&amp;L and sales
          revenue figures.
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <h2 className="border-b border-slate-100 px-5 py-3 text-sm font-semibold">By employee</h2>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-5 py-2">Staff</th>
              <th className="px-5 py-2">Count</th>
              <th className="px-5 py-2">Total</th>
              <th className="px-5 py-2">Average</th>
              <th className="px-5 py-2">% of sales</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.byEmployee.map((r) => (
              <tr key={r.userId ?? 'none'}>
                <td className="px-5 py-3 font-medium">{r.name}</td>
                <td className="px-5 py-3">{r.count}</td>
                <td className="px-5 py-3">{formatCurrency(r.total)}</td>
                <td className="px-5 py-3">{formatCurrency(r.average)}</td>
                <td className="px-5 py-3">
                  {r.tipsAsPercentOfSales != null ? `${r.tipsAsPercentOfSales}%` : '—'}
                </td>
              </tr>
            ))}
            {data.byEmployee.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-6 text-center text-slate-500">
                  No tips in this period.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <h2 className="border-b border-slate-100 px-5 py-3 text-sm font-semibold">By method</h2>
          <ul className="divide-y divide-slate-100 text-sm">
            {data.byMethod.map((r) => (
              <li key={r.method} className="flex justify-between px-5 py-3">
                <span className="capitalize">{r.method}</span>
                <span>
                  {r.count} · {formatCurrency(r.total)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <h2 className="border-b border-slate-100 px-5 py-3 text-sm font-semibold">By day</h2>
          <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto text-sm">
            {data.byDay.map((r) => (
              <li key={r.date} className="flex justify-between px-5 py-2">
                <span>{r.date}</span>
                <span>{formatCurrency(r.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
